import BaseAPI from "./BaseAPI";
import { labelTemplateRoute } from "../routes";

export default class LabelTemplateAPI extends BaseAPI {
    constructor(emitterInstance) {
        super(emitterInstance, labelTemplateRoute, "label-template");
    }

    /**
     * Render a draft template to HTML (NOT a PDF) for live previews.
     *
     * Hits POST /v2/label-templates/preview, which runs the same Go
     * html/template engine the PDF path uses but returns rendered
     * text/html — so previews match print fidelity without Gotenberg.
     * The server auto-fills placeholder QR/barcode/batch_id and caps the
     * payload at 512KB. The returned string is meant to be dropped into a
     * sandboxed <iframe srcdoc>, never injected into the app DOM.
     *
     * @param {Object} draft
     * @param {string} draft.html
     * @param {string} draft.css
     * @param {Object} draft.page  { width, height, unit, margin }
     * @param {Object} draft.sample_data  template root data
     * @param {string} [draft.product_id] real product used to assemble preview data
     * @param {boolean} [draft.box] render the box-label variant
     * @param {number} [draft.products_per_box] optional one-off box quantity
     * @returns {Promise<string>} rendered HTML document
     */
    async Preview({
        html,
        css,
        page,
        sample_data,
        product_id = "",
        box = false,
        products_per_box = 0
    }) {
        return this._request(
            "post",
            this._url("/preview"),
            { html, css, page, sample_data, product_id, box, products_per_box },
            { rawData: true, responseType: "text" }
        );
    }

    /**
     * Lists the 100 most recent products as compact options for real-data previews.
     * GET /v2/label-templates/preview-products.
     */
    async PreviewProducts() {
        return this._request("get", this._url("/preview-products"));
    }

    /**
     * Canonical Brother/Zebra label-size presets, served from the backend so
     * the editor and print dialog share one source of truth.
     * GET /v2/label-templates/presets.
     * @returns {Promise<Array>} preset objects { group, name, label, width, height, unit }
     */
    async Presets() {
        return this._request("get", this._url("/presets"));
    }

    /**
     * Soft-delete: adds the ARCHIVED tag (never hard-deletes, so products
     * produced with this template can still be re-rendered). Mirrors the
     * backend PUT /v2/label-templates/:id/archive.
     */
    async Archive(id) {
        const result = await this._request("put", this._url(`/${id}/archive`));
        this._emit("update", id);
        return result;
    }

    /** Removes the ARCHIVED tag. DELETE /v2/label-templates/:id/archive. */
    async Unarchive(id) {
        const result = await this._request("delete", this._url(`/${id}/archive`));
        this._emit("update", id);
        return result;
    }
}
